<template>
	<div class="">
		<h2>JAMES Keys Help</h2>
		<div class="text">
			JAMES has many translation strings and it can be difficult to know the right key to override.<br />
			<a href="#" class="link" @click.prevent="openBrowser()"><mdi information /> browse all keys with their standard values&hellip;</a><br />
			You can also conveniently search for keys there.
		</div>

		<Dialog ref="dialog" :width="825">
			<h1>JAMES translation strings</h1>
			<div class="body">
				<div class="toolbar">
					<input type="text" class="input search" placeholder="Filter by key or value" v-model="filter" />
					<select class="input locale" v-model="locale">
						<option v-for="l of locales" :key="l.code" :value="l.code">{{ l.name || l.code }}</option>
					</select>
				</div>

				<div v-if="loading" class="hint">Loading…</div>
				<div v-else-if="error" class="hint error">{{ error }}</div>
				<div v-else-if="!filtered.length" class="hint">No translation strings found.</div>
				<table v-else class="strings">
					<tr v-for="string of filtered" :key="string.id">
						<td class="key">
							<mdi content-copy class="copy" :title="copied == string.id ? 'Copied!' : 'Copy key'" @click="copy(string)" />
							<code>{{ string.key }}</code>
						</td>
						<td class="value">{{ string.values[locale] }}</td>
					</tr>
				</table>
			</div>
			<div class="buttons">
				<ActionButton class="cancel" @click="$refs.dialog.close()">Close</ActionButton>
			</div>
		</Dialog>
	</div>
</template>

<script>
// The strings live in the JAMES translation space and are read through the CDA,
// not the CMA the editor otherwise talks to: the editor's user has no CMA
// permissions on that space, but the delivery key below is public-read anyway.
export const extension = {
	type: 'EntryEditor',
	location: 'sidebarTop',
	contentTypes: [ 'translationString' ],
	// TODO: do not offer the comp in the translation space itself?
}

// entries: http://localhost:8081/spaces/5w7j2up2mnfw/environments/master/entries?contentType=translationString
const CDA_KEY = 'em4aDVrrG1hxPLR0ofFK4Ejo3tBPo69w'
const PAGE_SIZE = 1000

export default {
	props: { ctx: Object },
	data: () => ({
		loading: false,
		loaded: false,
		error: null,
		strings: [],
		locales: [],
		locale: null,
		filter: '',
		copied: null,
	}),
	computed: {
		// the space and environment of the URL we are in, not of the entry - the
		// keys to override are the ones this environment actually serves
		cdaBase() {
			const host = this.ctx.settings.baseEndpoint.replace('//cma.', '//cda.')
			return host + '/spaces/5w7j2up2mnfw/environments/master'
		},
		filtered() {
			const q = this.filter.trim().toLowerCase()
			if (!q) return this.strings
			return this.strings.filter(s =>
				s.key.toLowerCase().includes(q) ||
				(s.values[this.locale] ?? '').toLowerCase().includes(q))
		},
	},
	methods: {
		openBrowser() {
			this.$refs.dialog.open()
			if (!this.loaded) this.load()
		},
		async load() {
			this.loading = true
			this.error = null
			try {
				this.locales = await this.cdaGet('/locales?limit=100').then(r => r.items)
				this.locale = this.locales.find(l => l.default)?.code ?? this.locales[0]?.code

				// locale=* gives us every translation in one pass, so the language
				// selector switches without another round trip
				const strings = []
				for (let skip = 0; ; skip += PAGE_SIZE) {
					// no `order` - sorting by a localized field is not allowed
					// together with locale=*, so we sort below instead
					const page = await this.cdaGet('/entries?content_type=translationString&locale=*&limit=' + PAGE_SIZE + '&skip=' + skip)
					strings.push(...page.items.map(item => ({
						id: item.sys.id,
						// with locale=* even a non-localized field is a locale map
						key: Object.values(item.fields?.key ?? {})[0] ?? '',
						values: item.fields?.value ?? {},
					})))
					if (skip + PAGE_SIZE >= page.total) break
				}
				this.strings = strings.sort((a, b) => a.key.localeCompare(b.key))
				this.loaded = true
			}
			catch (e) {
				console.warn('JAMES keys: failed to load translation strings', e)
				this.error = 'Could not load the translation strings: ' + e.message
			}
			this.loading = false
		},
		async cdaGet(path) {
			const response = await fetch(this.cdaBase + path, {
				headers: { Authorization: 'Bearer ' + CDA_KEY },
			})
			if (!response.ok) throw new Error(response.status + ' ' + response.statusText)
			return await response.json()
		},
		copy(string) {
			navigator.clipboard.writeText(string.key).then(
				() => { this.copied = string.id },
				err => console.error('copy: Could not copy text: ', err))
		},
	},
}
</script>

<style scoped>
.text { line-height: 150%; }
.link { display: inline-block; margin-top: 5px; color: var(--primary); cursor: pointer; }

.toolbar { display: flex; gap: 10px; padding-top: 15px; position: sticky; top: 0; background: white; z-index: 1; }
.toolbar .search { flex: 1; }
.toolbar .locale { width: 200px; }

.hint { padding: 20px 0; color: #666; font-weight: normal; }
.hint.error { color: var(--color-red-mid); }

.strings { width: 100%; border-collapse: collapse; font-weight: normal; }
.strings td { padding: 7px 10px 7px 0; border-bottom: 1px solid #eee; vertical-align: top; }
.strings .key { width: 40%; }
.strings .key code { font-family: monospace; font-size: 12px; word-break: break-all; }
.strings .copy { fill: #999; cursor: pointer; margin-right: 6px; vertical-align: -3px; }
.strings .copy:hover { fill: var(--primary); }
.strings .value { width: 100%; }
</style>
